import React, { useState, useEffect } from 'react';

function UpgradeFailuresDashboard({ jwtToken }) {
  const [attempts, setAttempts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    upgrade_type: '',
    wallet: '',
    include_success: 'true',
    limit: 50
  });

  const fetchAttempts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/nft?action=upgrade-logs&${params}`,
        {
          headers: jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {}
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch upgrade attempts');
      }
      
      const data = await response.json();
      setAttempts(data.attempts || []);
      setStats(data.stats || {});
    } catch (error) {
      console.error('Error fetching upgrade attempts:', error);
      setAttempts([]);
      setStats({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttempts();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#28a745';
      case 'failed': return '#dc3545';
      case 'tx_wait_failed': return '#fd7e14';
      case 'user_cancelled': return '#6c757d';
      case 'started': return '#007bff';
      case 'mint_tx_pending': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const truncateAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Inter, sans-serif', color: 'white' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem' 
      }}>
        <h2 style={{ color: '#FFD700', margin: 0 }}>🚨 Upgrade Attempts Dashboard</h2>
        
        <button 
          onClick={fetchAttempts}
          disabled={loading}
          className="rainbowBtn"
          style={{ margin: 0 }}
        >
          {loading ? '⏳ Loading...' : '🔄 Refresh Data'}
        </button>
      </div>
      
      {/* Statistics Summary */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '20px', 
        marginBottom: '30px' 
      }}>
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.1)', 
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '20px', 
          borderRadius: '15px'
        }}>
          <h4 style={{ color: 'white', margin: '0 0 10px 0' }}>Total Attempts</h4>
          <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#FFD700' }}>
            {stats.total_attempts || 0}
          </div>
        </div>
        
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.1)', 
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '20px', 
          borderRadius: '15px'
        }}>
          <h4 style={{ color: 'white', margin: '0 0 10px 0' }}>Success Rate</h4>
          <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#28a745' }}>
            {stats.success_rate || 0}%
          </div>
        </div>

        <div style={{ 
          background: 'rgba(255, 255, 255, 0.1)', 
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '20px', 
          borderRadius: '15px'
        }}>
          <h4 style={{ color: 'white', margin: '0 0 10px 0' }}>Failed Attempts</h4>
          <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#dc3545' }}>
            {(stats.by_status?.failed || 0) + (stats.by_status?.tx_wait_failed || 0)}
          </div>
        </div>

        <div style={{ 
          background: 'rgba(255, 255, 255, 0.1)', 
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '20px', 
          borderRadius: '15px'
        }}>
          <h4 style={{ color: 'white', margin: '0 0 10px 0' }}>User Cancellations</h4>
          <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#6c757d' }}>
            {stats.by_status?.user_cancelled || 0}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ 
        background: 'rgba(255, 255, 255, 0.05)', 
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '20px', 
        borderRadius: '15px', 
        marginBottom: '20px'
      }}>
        <h4 style={{ color: 'white', margin: '0 0 15px 0' }}>Filters</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ color: 'white', display: 'block', marginBottom: '5px' }}>Status:</label>
            <select 
              value={filters.status} 
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="admin-select"
              style={{ 
                width: '100%', 
                padding: '8px', 
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white'
              }}
            >
              <option value="">All Statuses</option>
              <option value="failed">Failed</option>
              <option value="tx_wait_failed">TX Wait Failed</option>
              <option value="user_cancelled">User Cancelled</option>
              <option value="confirmed">Confirmed</option>
              <option value="started">Started</option>
              <option value="mint_tx_pending">TX Pending</option>
            </select>
          </div>

          <div>
            <label style={{ color: 'white', display: 'block', marginBottom: '5px' }}>Upgrade Type:</label>
            <select 
              value={filters.upgrade_type} 
              onChange={(e) => handleFilterChange('upgrade_type', e.target.value)}
              className="admin-select"
              style={{ 
                width: '100%', 
                padding: '8px', 
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white'
              }}
            >
              <option value="">All Types</option>
              <option value="single_upgrade">Single Upgrade</option>
              <option value="recipe_upgrade">Recipe Upgrade</option>
            </select>
          </div>

          <div>
            <label style={{ color: 'white', display: 'block', marginBottom: '5px' }}>Wallet:</label>
            <input 
              type="text"
              placeholder="0x..."
              value={filters.wallet} 
              onChange={(e) => handleFilterChange('wallet', e.target.value)}
              className="admin-input"
              style={{ 
                width: '100%', 
                padding: '8px', 
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white'
              }}
            />
          </div>

          <div>
            <label style={{ color: 'white', display: 'block', marginBottom: '5px' }}>Include Success:</label>
            <select 
              value={filters.include_success} 
              onChange={(e) => handleFilterChange('include_success', e.target.value)}
              className="admin-select"
              style={{ 
                width: '100%', 
                padding: '8px', 
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white'
              }}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>
        
        <button 
          onClick={fetchAttempts}
          disabled={loading}
          className="rainbowBtn"
          style={{ marginTop: '15px' }}
        >
          {loading ? '⏳ Loading...' : 'Apply Filters'}
        </button>
      </div>

      {/* Attempts Table */}
      <div style={{ 
        background: 'rgba(255, 255, 255, 0.05)', 
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '15px', 
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
            <tr>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', color: 'white' }}>
                Time
              </th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', color: 'white' }}>
                Wallet
              </th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', color: 'white' }}>
                Type
              </th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', color: 'white' }}>
                Status
              </th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', color: 'white' }}>
                Error
              </th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', color: 'white' }}>
                TX Hash
              </th>
            </tr>
          </thead>
          <tbody>
            {attempts.map((attempt) => (
              <tr key={attempt.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <td style={{ padding: '12px', color: 'white' }}>
                  {formatTimestamp(attempt.created_at)}
                </td>
                <td style={{ padding: '12px', fontFamily: 'monospace', color: 'white' }}>
                  {truncateAddress(attempt.wallet)}
                </td>
                <td style={{ padding: '12px', color: 'white' }}>
                  {attempt.upgrade_type.replace('_', ' ')}
                </td>
                <td style={{ padding: '12px' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.85em',
                    fontWeight: 'bold',
                    color: 'white',
                    background: getStatusColor(attempt.status)
                  }}>
                    {attempt.status.replace('_', ' ')}
                  </span>
                </td>
                <td style={{ padding: '12px', maxWidth: '200px', color: 'white' }}>
                  {attempt.error_message ? (
                    <span title={attempt.error_message}>
                      {attempt.error_message.length > 50 
                        ? `${attempt.error_message.substring(0, 50)}...`
                        : attempt.error_message
                      }
                    </span>
                  ) : '-'}
                </td>
                <td style={{ padding: '12px' }}>
                  {attempt.mint_tx_hash ? (
                    <a 
                      href={`https://polygonscan.com/tx/${attempt.mint_tx_hash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#FFD700', textDecoration: 'none' }}
                    >
                      {truncateAddress(attempt.mint_tx_hash)}
                    </a>
                  ) : <span style={{ color: 'white' }}>-</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {attempts.length === 0 && !loading && (
          <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
            No upgrade attempts found with current filters.
          </div>
        )}
      </div>

      {/* Status Breakdown */}
      {stats.by_status && (
        <div style={{ 
          marginTop: '30px',
          background: 'rgba(255, 255, 255, 0.05)', 
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '20px', 
          borderRadius: '15px'
        }}>
          <h4 style={{ color: 'white', margin: '0 0 15px 0' }}>Status Breakdown</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {Object.entries(stats.by_status).map(([status, count]) => (
              <div key={status} style={{
                padding: '8px 12px',
                borderRadius: '4px',
                background: getStatusColor(status),
                color: 'white',
                fontSize: '0.9em',
                fontWeight: 'bold'
              }}>
                {status.replace('_', ' ')}: {count}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default UpgradeFailuresDashboard; 